import {useMutation, useSuspenseQuery} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {useState} from "react";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {FiAlertCircle, FiCheck, FiMail, FiPlus} from "react-icons/fi";
import * as yup from "yup";

import {Button} from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {captureMessageForReporting} from "@/lib/errorReporting.tsx";
import {
  AccountUsersQueryResponse,
  GQL_ACCOUNTUSER_DELETE,
  GQL_ACCOUNTUSER_RESEND_INVITE,
  GQL_ACCOUNTUSER_UPDATE,
  GQL_ACCOUNTUSERS_QUERY,
  UpdateAccountUserResult,
} from "@/lib/queries/accounts.ts";
import {UserAccessLevel, UserAccessLevelLabels} from "@/lib/types/accounts.ts";
import {GraphQLResponse, QueryError, ResponseStatus} from "@/lib/types/common.ts";
import {getLocalData, setLocalData} from "@/pages/onboardingwizard/utils.ts";

import {ButtonRow} from "../buttonrow/ButtonRow.tsx";
import {StepBodyProps} from "../step/Step.tsx";

import styles from "./StepTeamBody.module.scss";

const minRows = 2;
const maxRows = 5;

type Row = {
  accountUserId?: number;
  firstName: string;
  lastName: string;
  email: string;
  accessLevel: string;
};

type FormValues = {
  rows: Row[];
};

type RowKeys = keyof Row;

const defaultRow: Row = {
  firstName: "",
  lastName: "",
  email: "",
  accessLevel: UserAccessLevel.READ,
};

const FormSchema = yup.object({
  rows: yup
    .array()
    .of(
      yup.object().shape({
        firstName: yup.string().required("First name is required"),
        lastName: yup.string().required("Last name is required"),
        email: yup.string().email("Invalid email address").required("Email is required"),
        accessLevel: yup.string().required("Access level is required"),
      })
    )
    .default([{...defaultRow}]),
});

const accessLevelOptions = Object.keys(UserAccessLevelLabels).map((key) => ({
  value: key,
  label: UserAccessLevelLabels[key as UserAccessLevel],
}));

export const StepTeamBody = ({onSubmit, stepsCount, activeStepNr}: StepBodyProps) => {
  const {user} = useCurrentUserContext();
  const {
    data: {
      accountUsers: {items},
    },
    refetch,
  } = useSuspenseQuery<AccountUsersQueryResponse>(GQL_ACCOUNTUSERS_QUERY, {
    variables: {
      sort: "id",
      limit: maxRows + 1,
    },
  });
  const [updateAccountUser] = useMutation<UpdateAccountUserResult>(GQL_ACCOUNTUSER_UPDATE);
  const [deleteAccountUser] = useMutation<GraphQLResponse<null>>(GQL_ACCOUNTUSER_DELETE);
  const [reinviteAccountUser] = useMutation<GraphQLResponse<null>>(GQL_ACCOUNTUSER_RESEND_INVITE);
  const [invitesSent, setInvitesSent] = useState<string[]>([]);

  const getDefaultRows = (checkLocalStorage?: boolean) => {
    if (checkLocalStorage && user) {
      const localData = getLocalData(user.id, activeStepNr);
      if (localData) {
        return localData;
      }
    }
    const defaultMembers: Row[] = items
      .filter(({id}) => id !== user?.id)
      .map(({id, first_name, last_name, email, access_level}) => ({
        accountUserId: id,
        firstName: first_name,
        lastName: last_name,
        email,
        accessLevel: access_level,
      }))
      .splice(0, maxRows);

    while (defaultMembers.length < maxRows) {
      defaultMembers.push({...defaultRow});
      if (defaultMembers.length >= minRows) {
        break;
      }
    }
    return defaultMembers;
  };

  const methods = useForm<FormValues>({
    defaultValues: {
      rows: getDefaultRows(true),
    },
    resolver: yupResolver<FormValues>(FormSchema),
  });
  const {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: {errors},
  } = methods;

  const {fields, append} = useFieldArray({
    control,
    name: "rows",
  });

  const addRow = () => {
    if (fields.length < maxRows) {
      append({...defaultRow});
    }
  };

  const showRowErrors = (rowIdx: number, response: QueryError): boolean => {
    let setErrorResult = false;
    const fieldNames: RowKeys[] = ["firstName", "lastName", "email"];
    const unhandledErrors: Record<string, string[]> = {};
    Object.keys(response.message).forEach((key) => {
      if (fieldNames.includes(key as RowKeys)) {
        setErrorResult = true;
        setError(`rows.${rowIdx}.${key}` as `rows.${number}.${RowKeys}`, {message: response.message[key][0]});
      } else {
        unhandledErrors[key] = response.message[key];
      }
    });
    if (Object.keys(unhandledErrors).length !== 0) {
      captureMessageForReporting(
        `Unhandled errors when inviting account user on StepTeamBody: ${JSON.stringify(unhandledErrors)}`
      );
    }
    return setErrorResult;
  };

  const submitRow = async ({accountUserId, firstName, lastName, email, accessLevel}: Row) => {
    if (firstName === "" && lastName === "" && email === "") {
      return deleteAccountUser({
        variables: {
          id: accountUserId,
        },
      });
    } else {
      return updateAccountUser({
        variables: {
          data: {
            id: accountUserId,
            first_name: firstName,
            last_name: lastName,
            email,
            access_level: accessLevel,
          },
        },
      });
    }
  };

  const sendInvites = async (forceReinvite?: boolean) => {
    // Validate only non-empty rows
    let isValid = true;
    const rowsToBeSaved = getValues()
      .rows.map((row, idx) => ({...row, idx})) // Add index to each row to keep track of the original row number
      .filter(
        ({firstName, lastName, email, accountUserId}) =>
          firstName !== "" || lastName !== "" || email !== "" || accountUserId
      );
    if (rowsToBeSaved.length === 0) {
      return;
    }
    for (const row of rowsToBeSaved) {
      clearErrors(`rows.${row.idx}`);
      if (rowIsEmpty(row.idx)) {
        continue;
      }
      const valid = await trigger(`rows.${row.idx}`);
      if (!valid) {
        isValid = false;
      }
    }
    if (!isValid) {
      return true;
    }

    // Save rows in parallel
    const results = await Promise.all(rowsToBeSaved.map((row) => submitRow(row)));
    const previousData = getDefaultRows();
    const {data} = await refetch();
    let hasErrors = false;
    const invitedEmails: string[] = [];
    results.forEach((result, idx) => {
      const originalIdx = rowsToBeSaved[idx].idx;
      if (result.data?.response.status === ResponseStatus.ERROR) {
        const response = result.data.response as QueryError;
        const setErrorResult = showRowErrors(originalIdx, response);
        if (!hasErrors) {
          hasErrors = setErrorResult;
        }
      } else if (result.data?.response.status === ResponseStatus.SUCCESS) {
        clearErrors(`rows.${originalIdx}`);
        const newEmail = rowsToBeSaved[idx].email;
        const newAccountUserId = data.accountUsers.items.find(({email}) => email === newEmail)?.id;
        setValue(`rows.${originalIdx}.accountUserId`, newAccountUserId);
        if (newEmail) {
          const prevRow = previousData?.[originalIdx];
          const {accountUserId, email} = prevRow || {};
          if (accountUserId && (email !== newEmail || forceReinvite)) {
            // Reinvite user if email was changed or if forceReinvite is set
            reinviteAccountUser({
              variables: {
                id: accountUserId,
              },
            });
          }
          invitedEmails.push(newEmail);
        }
      }
    });
    setInvitesSent(invitedEmails);
    return hasErrors;
  };

  const submitStep = async (back?: boolean) => {
    setLocalData(user?.id, activeStepNr, back ? getValues().rows : undefined);
    if (back) {
      return onSubmit(activeStepNr - 1, true);
    }
    const hasErrors = await sendInvites();
    if (!hasErrors) {
      onSubmit(activeStepNr + 1, true);
    }
  };

  const rowIsEmpty = (rowIdx: number) => {
    const {firstName, lastName, email} = getValues(`rows.${rowIdx}`);
    return firstName === "" && lastName === "" && email === "";
  };

  const handleBlur = (rowIdx: number, fieldName: RowKeys) => {
    if (rowIsEmpty(rowIdx)) {
      return;
    }
    trigger(`rows.${rowIdx}.${fieldName}`);
  };

  const handleChange = (rowIdx: number, fieldName: RowKeys) => {
    if (!errors.rows?.[rowIdx]) {
      return;
    }
    if (rowIsEmpty(rowIdx)) {
      clearErrors(`rows.${rowIdx}`);
    } else {
      clearErrors(`rows.${rowIdx}.${fieldName}`);
    }
  };

  return (
    <div className="pulse-color-gray-900">
      <FormProvider {...methods}>
        <form id="onboarding-team-form">
          <div className={styles.rowsWrapper}>
            <div className={styles.rowsGroup}>
              {fields.map((field, index) => (
                <div key={field.id} className={styles.rowWrapper}>
                  <div className={styles.row}>
                    <InputField
                      className="w-100"
                      name={`rows.${index}.firstName`}
                      label="First Name"
                      placeholder="First Name"
                      options={{
                        onChange: () => handleChange(index, "firstName"),
                        onBlur: () => handleBlur(index, "firstName"),
                      }}
                      hideErrorMessage
                    />
                    <InputField
                      className="w-100"
                      name={`rows.${index}.lastName`}
                      label="Last Name"
                      placeholder="Last Name"
                      options={{
                        onChange: () => handleChange(index, "lastName"),
                        onBlur: () => handleBlur(index, "lastName"),
                      }}
                      hideErrorMessage
                    />
                    <InputField
                      className="w-100"
                      name={`rows.${index}.email`}
                      label="Email"
                      placeholder="Email Address"
                      options={{
                        onChange: () => handleChange(index, "email"),
                        onBlur: () => handleBlur(index, "email"),
                      }}
                      hideErrorMessage
                    />
                    <SelectField
                      className={styles.select}
                      isSearchable={false}
                      name={`rows.${index}.accessLevel`}
                      options={accessLevelOptions}
                    />
                  </div>
                  {index < fields.length - 1 && <Divider className={styles.divider} />}
                </div>
              ))}
            </div>
            <div className={styles.buttonRowMobile}>
              {fields.length < maxRows && (
                <Button variant="secondary" onClick={addRow}>
                  <Icon icon={FiPlus} /> Add Another
                </Button>
              )}
              <Button variant="secondary" onClick={() => sendInvites(true)} disabled={!!errors.rows?.length}>
                <Icon icon={FiMail} /> Send Invites
              </Button>
            </div>
            {errors.rows?.length && (
              <div className={styles.error}>
                <Icon icon={FiAlertCircle} className="me-1" display="inline" removeDivDimensions />
                Invalid data provided - check highlighted fields.
              </div>
            )}
            {invitesSent.length > 0 && (
              <div className={styles.results}>
                <Icon icon={FiCheck} className="pulse-color-green-500 me-1" display="inline" removeDivDimensions />
                <span className="pulse-color-gray-600">Invites sent to </span>
                <span className="pulse-color-blue-500">{invitesSent.join(", ")}</span>
              </div>
            )}
            {fields.length >= maxRows && (
              <div className={styles.info}>
                To invite additional team members, add more under Settings &gt; User Management.
              </div>
            )}
            <div className={styles.buttonRow}>
              {fields.length < maxRows && (
                <Button variant="secondary" onClick={addRow}>
                  <Icon icon={FiPlus} /> Add Another
                </Button>
              )}
              <Button
                className={styles.sendInvitesButton}
                variant="secondary"
                onClick={() => sendInvites(true)}
                disabled={!!errors.rows?.length}
              >
                <Icon icon={FiMail} /> Send Invites
              </Button>
            </div>
          </div>
          <ButtonRow
            onSubmit={submitStep}
            stepsCount={stepsCount}
            activeStepNr={activeStepNr}
            isNextDisabled={!!errors.rows?.length}
          />
        </form>
      </FormProvider>
    </div>
  );
};
